import type {
  CellClickedEvent,
  GridApi,
  SelectionChangedEvent,
} from 'ag-grid-community';
import classNames from 'classnames';
import { union } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { ProjectId, TaskIdV2 } from 'api/models';
import { OnGridReady } from 'components/atoms/grid/plugins/use-on-grid-ready';
import { TableSkeleton } from 'components/atoms/table';
import { RootState } from 'data';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import {
  ClickEventTags,
  LogType,
  useLoadLoggerForDataDog,
} from 'hooks/use-data-dog-logger';
import { InitRouter } from 'initRouter';
import { Empty } from 'stores/core';
import type { Task } from 'stores/tasks';
import { isChildNode } from 'utils/is-child-node';
import type { GridChangeEvent } from '../grid-custom-column';
import { useTaskDrawer } from '../task-drawer';
import { BulkEditor } from '../tasks/bulk-editor';
import { Flex, LoadingOverlay, Wrapper } from '../tasks/components';
import type { FilterFunction } from '../tasks/filter-functions/types';
import { useDeleteTasks } from '../tasks/use-delete-tasks';
import { useUpdateTasks } from '../tasks/use-update-tasks';
import { toRows } from '../tasks/utils';
import { Grid } from './grid';
import { DEFAULT_GRID_OPTIONS } from './grid-options';

const StyledGrid = styled(Grid)`
  .ag-row {
    border-color: var(--table-border-color);
  }
  .ag-react-container {
    max-width: 100%;
  }
  .multiline-text {
    max-height: var(--height-lg);
    padding-top: var(--spacing-02);
    white-space: pre-wrap;
    line-height: var(--spacing-07);
  }
  .task-grid-row-private {
    --ag-odd-row-background-color: var(--private-01);
    --ag-background-color: var(--private-01);
    --ag-row-hover-color: var(--private-02);
  }
  .ag-cell-focus {
    border: none !important;
  }
  [col-id='status'].ag-cell-inline-editing {
    border-radius: 0;
    height: 100%;
    border: 2px solid var(--primary-color) !important;
    > div {
      padding-left: var(--group-cell-padding-left);
    }
  }
`;

interface Props {
  onGridReady?: OnGridReady;
  initialized: boolean;
  filterFunction: FilterFunction | null;
  gridApi?: GridApi;
  handleGridChange?: (data: GridChangeEvent) => void;
  selected: Task[];
  setSelected: (selections: Task[]) => void;
  projectId: number;
}

export const TaskGrid: FC<Props> = ({
  initialized,
  filterFunction,
  selected,
  gridApi,
  setSelected,
  onGridReady,
  handleGridChange = () => {},
  projectId,
}) => {
  const { updateMultipleTasks } = useUpdateTasks();
  const onDelete = useDeleteTasks(false);
  const projectResource = useProjectAsRBACResource(projectId);

  const { hasAccessToResource } = useWithAccess();

  const canCreateTask = hasAccessToResource({
    rule: 'TASK:CREATE',
    resourceData: projectResource,
  });
  const provisionalRow = useMemo(
    () =>
      !canCreateTask
        ? undefined
        : {
            project: projectId,
            taskId: 'provisional',
            taskTree: [{ taskId: 'provisional' }],
          },
    [canCreateTask, projectId],
  );
  const { byProjectId, byId: lookup } = useSelector(
    ({ tasks_v2 }: RootState) => tasks_v2,
  );
  const ids = useMemo(
    () => byProjectId[projectId] ?? Empty(),
    [byProjectId, projectId],
  );
  const isTasksLoading = useMemo(
    () => ids[0] !== 'loaded' && ids[0] !== 'refreshing',
    [ids],
  );
  const [rows, setRows] = useState<Record<string, unknown>[]>();

  /**
   * sortIds() sorts the ids so that the subtasks
   * are in the right order, ag-grid renders the rows as
   * we give it so we make it ordered here.
   */

  const sortIds = useCallback((idlist, lookuplist) => {
    const queue: number[] = [];
    const result: number[] = []; // array of ids that get returned

    if (!idlist || !lookuplist) {
      return [];
    }
    // pushing the parents alone into the queue
    queue.push(...idlist.filter((id) => !lookuplist[id][1]?.parentTask));

    // iteratively adding all their children into queue then into the arrray
    while (queue.length > 0) {
      const id = queue.shift() as number;

      if (!result.includes(id)) {
        result.push(id);
      }
      if (lookuplist[id]?.[1]?.subTaskOrder) {
        queue.push(...(lookuplist[id][1]?.subTaskOrder as number[]));
      }
    }

    return result;
  }, []);

  useEffect(() => {
    if (!initialized || isTasksLoading) {
      return;
    }
    if (!ids) {
      return;
    }

    const sortedIds = sortIds(ids[1] ?? [], lookup); // sortIds returns ids of the subtasks in the right order

    setRows(toRows(sortedIds, lookup));
  }, [ids, lookup, sortIds, initialized, isTasksLoading]);

  const [[taskDrawerState], showTaskDrawer, hideTaskDrawer] = useTaskDrawer();

  const onSelectionChanged = useCallback(
    ({ api }: SelectionChangedEvent) => {
      const selectedData = api.getSelectedNodes().reduce((acc, node) => {
        if (node.id != null) {
          acc.push(node.data);
        }

        return acc;
      }, [] as Task[]);

      if (selectedData.length !== 0 && taskDrawerState === 'show') {
        hideTaskDrawer();
      }
      setSelected(selectedData);
    },
    [hideTaskDrawer, setSelected, taskDrawerState],
  );

  const onCellClicked = useCallback(
    (event: CellClickedEvent) => {
      if (event.colDef.field !== 'status') {
        return false;
      }
      if (
        event.node.id === 'provisional' &&
        event.rowIndex != null &&
        event.column.getColId() === 'status'
      ) {
        event.api.startEditingCell({
          colKey: event.column,
          rowIndex: event.rowIndex,
        });

        return true;
      }
      const target = event.event?.target as HTMLElement;

      if (isChildNode(['task-field-value'])(target) && event.rowIndex != null) {
        event.api.startEditingCell({
          colKey: event.column,
          rowIndex: event.rowIndex,
        });
      } else {
        event.api.clearFocusedCell();
        const task = event.data as Task;

        showTaskDrawer(ProjectId(task.project), TaskIdV2(task.taskId));
      }

      return true;
    },
    [showTaskDrawer],
  );

  const projectIds = useMemo(
    () =>
      selected
        .reduce(
          (project_ids, task) => union(project_ids, [task!.project]),
          [] as number[],
        )
        .map(ProjectId),
    [selected],
  );

  const isPrivate = useMemo(
    () => selected.some((task) => task!.private),
    [selected],
  );

  const getLookupForSelectedItem = useCallback(() => lookup, [lookup]);

  const { pathname } = useLocation();
  const { executeLogger: executeLoggerForPageLoad } = useLoadLoggerForDataDog(
    InitRouter.getInstance().tagNames(pathname),
    LogType.CallbackLog,
    true,
  );

  const { executeLogger: executeLoggerForFilterChange } =
    useLoadLoggerForDataDog(
      [ClickEventTags.ProjectTaskListFilterChange],
      LogType.CallbackLog,
    );

  const { executeLogger: executeLoggerForFieldUpdate } =
    useLoadLoggerForDataDog([ClickEventTags.FieldEdit], LogType.CallbackLog);

  if (isTasksLoading) {
    return (
      <LoadingOverlay>
        <TableSkeleton rowCount={20} columnCount={8} />
      </LoadingOverlay>
    );
  }

  return (
    <Wrapper>
      <Flex
        className={classNames({
          'ag-theme-balham': true,
          'multi-select': selected.length > 0,
        })}
      >
        <StyledGrid
          {...DEFAULT_GRID_OPTIONS}
          alwaysShowHorizontalScroll
          alwaysShowVerticalScroll
          isPaginated={false}
          loading={!initialized || isTasksLoading}
          rowData={provisionalRow != null ? rows?.concat(provisionalRow) : rows}
          filter={
            filterFunction
              ? (record) => provisionalRow === record || filterFunction(record)
              : undefined
          }
          onCellClick={onCellClicked}
          onSelectionChanged={onSelectionChanged}
          onGridReady={onGridReady}
          onColumnVisible={handleGridChange}
          onColumnMoved={handleGridChange}
          onNewColumnsLoaded={handleGridChange}
          onSortChanged={handleGridChange}
          onFirstDataRendered={executeLoggerForPageLoad}
          onFilterChanged={executeLoggerForFilterChange}
          onCellEditingStopped={executeLoggerForFieldUpdate}
        />

        {isTasksLoading ? (
          <LoadingOverlay>
            <TableSkeleton rowCount={20} columnCount={8} />
          </LoadingOverlay>
        ) : null}
      </Flex>
      <BulkEditor
        ids={selected.map((x) => String(x.taskId))}
        getLookup={getLookupForSelectedItem}
        onUpdate={updateMultipleTasks}
        onDelete={onDelete}
        onClose={() => gridApi?.deselectAll()}
        projectIds={projectIds}
        isPrivate={isPrivate}
      />
    </Wrapper>
  );
};
